import mixpanelBrowser, { Callback, Mixpanel } from 'mixpanel-browser';
import { MIXPANEL_API_KEY } from 'utils/constants/mixpanel';

type UserInfo = Parameters<typeof mixpanelBrowser.people.set>[0];

function isPwnHealthPage(): boolean {
  return typeof window !== 'undefined' && Boolean(window.isPwnHealthPage);
}

export namespace mixpanel {
  let initialized = false;

  export type TrackCallback = Callback;

  let people: Mixpanel['people'] | null;

  export const init = () => {
    if (MIXPANEL_API_KEY && !initialized) {
      // Mixpanel is classified Essential per legal review (IN2-4538): it fires
      // regardless of cookie-banner choices, including on embedded iOS app
      // pages. It stays off only on PWN Health white-label pages where we
      // don't need page-tracking data.
      const disabled = isPwnHealthPage();

      mixpanelBrowser.init(MIXPANEL_API_KEY, {
        opt_out_tracking_by_default: disabled,
        autocapture: {
          pageview: 'full-url',
          click: true,
          input: true,
          scroll: true,
          submit: true,
          capture_text_content: true,
        },
        flags: true,
        // Configure session recording but don't auto-start it
        record_sessions_manually: true,
        record_mask_text_selector: '[data-mask-text]',
        record_mask_text_class: 'mask-text',
        // Enable heatmap data collection
        record_heatmap_data: true,
        // Collect fonts for better session replay rendering
        record_collect_fonts: true,
      } as any);

      if (disabled) {
        mixpanelBrowser.opt_out_tracking();
      } else if (mixpanelBrowser.has_opted_out_tracking()) {
        // Returning visitor with an opt-out persisted while Mixpanel was gated
        // on the Osano ANALYTICS category (IN2-4221). Mixpanel has no
        // user-facing opt-out anymore (it's Essential), so that "0" is stale.
        // opt_in_tracking() writes the "1" marker, re-enables persistence, and
        // flushes queued events - unlike clear_opt_in_out_tracking(), which did
        // not reliably remove the persisted "0" on init and left these visitors
        // permanently dark. Guarded on has_opted_out_tracking() so we don't emit
        // a `$opt_in` event on every page load for already-opted-in visitors.
        // Revisit if a user-initiated Mixpanel opt-out is ever (re)introduced.
        mixpanelBrowser.opt_in_tracking();
      }

      people = mixpanelBrowser.people;

      initialized = true;
    }
  };

  export const setPeople = function (userInfo: UserInfo) {
    people?.set(userInfo);
  };

  export const identify: Mixpanel['identify'] = function (...args) {
    mixpanelBrowser.identify(...args);
  };

  export const track: Mixpanel['track'] = function (...args) {
    mixpanelBrowser.track(...args);
  };

  export const getVariantValue = async (
    flagName: string,
    fallback?: string,
  ): Promise<string | undefined> => {
    try {
      return await mixpanelBrowser.flags.get_variant_value(flagName, {
        fallback,
      });
    } catch (error) {
      return fallback;
    }
  };

  /**
   * Feature-flag variant. Evaluating a live flag records Mixpanel's
   * `$experiment_started` exposure. A missing flag or a thrown SDK returns
   * the fallback and does not redirect callers.
   */
  /** In-memory flag, or null when Mixpanel has not finished loading flags. */
  export const getFlagVariantSync = (
    flagName: string,
    fallbackKey = 'control',
  ): { key: string; value: unknown } | null => {
    try {
      init();
      if (!mixpanelBrowser.flags?.are_flags_ready?.()) return null;
      const variant = mixpanelBrowser.flags.get_variant_sync(flagName, {
        key: fallbackKey,
        value: fallbackKey,
      });
      if (!variant || typeof variant.key !== 'string') return null;
      return variant;
    } catch {
      return null;
    }
  };

  export const getFlagVariant = async (
    flagName: string,
    fallbackKey = 'control',
  ): Promise<{ key: string; value: unknown }> => {
    const fallback = { key: fallbackKey, value: fallbackKey };
    try {
      init();
      const variant = await mixpanelBrowser.flags.get_variant(
        flagName,
        fallback,
      );
      if (!variant || typeof variant.key !== 'string') return fallback;
      return variant;
    } catch {
      return fallback;
    }
  };

  /** Register super properties once (for experiment attribution on funnel events). */
  export const registerOnce = (props: Record<string, unknown>) => {
    try {
      mixpanelBrowser.register_once(props);
    } catch {
      // ignore
    }
  };

  /** Register/overwrite super properties on every call (use for experiment variant attribution). */
  export const register = (props: Record<string, unknown>) => {
    try {
      mixpanelBrowser.register(props);
    } catch {
      // ignore
    }
  };

  // Session recording methods
  export const startRecording = () => {
    if (initialized && mixpanelBrowser.start_session_recording) {
      mixpanelBrowser.start_session_recording();
    }
  };

  export const stopRecording = () => {
    if (initialized && mixpanelBrowser.stop_session_recording) {
      mixpanelBrowser.stop_session_recording();
    }
  };

  export const isRecording = (): boolean => {
    if (initialized && mixpanelBrowser.get_session_recording_properties) {
      const props = mixpanelBrowser.get_session_recording_properties();
      return props &&
        typeof props === 'object' &&
        '$session_recording_enabled' in props
        ? props['$session_recording_enabled' as keyof typeof props] === true
        : false;
    }
    return false;
  };
}
